import type { JwtTokens, TokenClaims, Workspace } from "@/definitions/types";
import { CanceledError } from "axios";
import { jwtDecode } from "jwt-decode";
import { ref } from "vue";
import router from "@/router";
import { useAuthStore } from "@/stores/auth";
import {
  applyWorkspaceSwitch,
  currentPathForRedirect,
  goLoginPage,
  isExpiredToken,
  lastQuietRenewalStatus,
  LOGIN_REASON_EXPIRED,
  LOGIN_REASON_REMOVED,
  type LoginReason,
  quietRenewalNotJoinedAnyWorkspace,
  quietRenewalRevokedWorkspace,
  recoverFromWorkspaceRemoval,
  resetQuietRenewalResult,
  workspaceRemovedMessage,
} from "@/utils/commands";
import { logger } from "@/utils/logger";
import { STORAGE_KEYS, tokenStorage } from "@/utils/storage";
import { toastSuccess, toastWarning } from "@/utils/toaster";

/**
 * 탭 세션 지킴이(30차 S30-01 · S30-02)
 *
 * 토큰은 같은 브라우저의 탭들이 함께 쓰는 localStorage 에 있고, 화면 상태(pinia `auth`)는 탭마다 메모리에 따로 있다. 그래서 다른 탭이
 * 워크스페이스를 바꾸거나 로그아웃 · 다른 계정으로 로그인하면, 이 탭(옛 워크스페이스 화면)의 요청이 다른 워크스페이스 · 다른 계정 토큰으로
 * 나가 **다른 워크스페이스에 저장됐다**. 이 탭이 재발급하면 반대 방향으로 다른 탭이 같은 일을 겪었다.
 * - 각 탭의 고정값은 이 탭 스토어의 사용자 id(`info.id`) · 워크스페이스 id(`workspace.id`)다. 스토어는 탭을 열 때 한 번 읽고 그 뒤로는 이
 *   탭의 동작(로그인 · 전환 `applyWorkspaceSwitch` · 소속 해제 강제 전환)으로만 바뀐다 — 정상 전환은 고정값을 함께 옮긴다.
 * - 요청을 보내기 전(`apis.ts` 요청 인터셉터 · `getValidatedAccessToken` · 재발급)마다 공용 토큰의 클레임을 고정값과 대조한다. 다르면 읽기 ·
 *   쓰기 모두 보내지 않고(읽기도 틀린 데이터를 그린다) 상단 안내(`SessionNotice.vue`)로 새로고침을 권한다.
 * - `storage` 이벤트로 다른 탭의 토큰 변경을 바로 알아 같은 안내를 띄운다. 같은 사용자 · 같은 워크스페이스로의 회전(다른 탭의 재발급)은 받아들여
 *   이 탭 스토어의 토큰도 맞춘다. 공용 토큰이 다시 이 탭과 같은 세션이 되면 안내를 거둔다.
 *
 * 세션 만료(리프레시까지 만료 · 401)로 로그인 화면에 보낼 때는 지금 화면에 저장하지 않은 편집이 있는지 먼저 본다(`expireSession`). 있으면
 * 이동하지 않고 같은 상단 안내로 "다시 로그인하면 편집이 사라진다" 를 알리고, 사용자가 내용을 복사한 뒤 「로그인으로 이동」 을 누르게 한다.
 * 화면 사용 중(입력 · 클릭 · 키 · 화면 표시)에는 만료 전에 미리 재발급해 리프레시가 끊기지 않게 한다(`renewWhileActive`).
 *
 * 지금 워크스페이스에서 제거돼 다른 소속으로 강제 전환할 때(4차 W5-02)도 저장하지 않은 편집이 있으면 곧바로 옮기지 않는다(62차 BC62-01,
 * `deferWorkspaceRemoval`). 전환은 화면을 다시 마운트해 편집을 버리므로, 같은 상단 안내로 복사할 기회를 준 뒤 「○○ 워크스페이스로 전환」 을
 * 누르게 한다.
 */

/**
 * 상단 안내 종류. `expired` 는 이 탭의 세션 만료, `removed` 는 이 탭의 워크스페이스에서 제거된 것(전환 대기)이고, 나머지는 다른 탭이
 * 공용 토큰을 바꾼 경우다.
 */
export type SessionNoticeKind =
  "workspace" | "account" | "signed-out" | "expired" | "removed";

export interface SessionNotice {
  kind: SessionNoticeKind;
  /** 다시 로그인한 뒤 돌아올 화면(`expired` 만) */
  redirect?: string;
  /** 옮겨 갈 워크스페이스 이름(`removed` 만 — 없으면 소속이 하나도 남지 않은 제거라 로그인으로 보낸다) */
  workspaceName?: string;
}

/** 지금 띄울 안내. 있으면 이 탭의 모든 API 요청을 보내지 않는다. */
export const sessionNotice = ref<SessionNotice | null>(null);

/** 요청을 막을 때의 토스트 문구(상단 안내가 이미 있어 짧게 한 번 더 짚는다) */
const BLOCKED_MESSAGES: Record<SessionNoticeKind, string> = {
  workspace:
    "다른 탭에서 워크스페이스가 바뀌어 이 탭의 요청을 보내지 않았습니다. 이 화면을 새로고침해 주세요.",
  account:
    "다른 탭에서 다른 계정으로 로그인해 이 탭의 요청을 보내지 않았습니다. 이 화면을 새로고침해 주세요.",
  "signed-out":
    "다른 탭에서 로그아웃해 이 탭의 요청을 보내지 않았습니다. 이 화면을 새로고침해 주세요.",
  expired:
    "세션이 만료되어 저장할 수 없습니다. 필요한 내용을 복사한 뒤 상단 「로그인으로 이동」 을 눌러 주세요.",
  removed:
    "이 워크스페이스에서 제거되어 저장할 수 없습니다. 필요한 내용을 복사한 뒤 상단 전환 버튼을 눌러 주세요.",
};

/** 소속이 하나도 없는 제거(`deferRemovalWithoutWorkspace`)의 차단 문구 — 전환할 곳이 없어 「로그인으로 이동」 으로 안내한다 */
const BLOCKED_MESSAGES_NO_WORKSPACE =
  "이 워크스페이스에서 제거되어 저장할 수 없습니다. 필요한 내용을 복사한 뒤 상단 「로그인으로 이동」 을 눌러 주세요.";

const BLOCKED_TOAST_INTERVAL_MS = 4_000;
let lastBlockedToastAt = 0;

/**
 * 공용 액세스 토큰이 이 탭의 세션(고정값)과 다른가. 이 탭에 세션이 없으면(로그인 전 · 정리 뒤) 비교할 고정값이 없어 null 이다.
 * Admin 토큰은 `workspaceId` 가 null 이고 스토어 `workspace.id` 는 0 이라 둘 다 0 으로 맞춰 비교한다.
 */
function tabConflictOf(
  accessToken: string | null,
): Exclude<SessionNoticeKind, "expired" | "removed"> | null {
  const { info, workspace } = useAuthStore();
  if (!info.id) return null;
  if (!accessToken) return "signed-out";
  let claims: TokenClaims;
  try {
    claims = jwtDecode<TokenClaims>(accessToken);
  } catch {
    return "signed-out";
  }
  if (claims.id !== info.id || (claims.type && claims.type !== info.type)) {
    return "account";
  }
  if ((claims.workspaceId ?? 0) !== (workspace.id || 0)) return "workspace";
  return null;
}

/**
 * 공용 토큰을 이 탭의 고정값과 대조해 안내를 맞추고, 요청을 막아야 하면 그 종류를 돌려준다.
 * - 다르면 다른 탭이 바꾼 것이다 — 그 종류로 안내한다(만료 안내보다 앞선다. 새로고침이 둘 다 푼다). 다만 만료 안내 중에 공용 토큰이
 *   지워진 것(다른 탭의 로그아웃 · 만료 처리)은 같은 결론이라 만료 안내를 그대로 둔다 — "새로고침" 으로 바꾸면 편집을 잃는다(수정 라운드 L6).
 * - 같고 이 탭 스토어와 다른 토큰이면(다른 탭의 재발급 · 같은 계정 재로그인) 리프레시가 살아 있을 때 받아들인다. 다른 탭 안내를 거두고,
 *   만료 안내도 거둔다 — 다른 탭에서 같은 계정 · 같은 워크스페이스로 다시 로그인했으니 이 화면에서 이어서 저장할 수 있다.
 * - 충돌이 없어졌으면(이 탭이 스스로 공용 토큰과 같아진 경우 포함) 다른 탭 안내는 거둔다. 만료 안내는 새 토큰을 받았을 때만 거둔다(수정 라운드 M1).
 */
function syncWithSharedTokens(): SessionNoticeKind | null {
  const accessToken = tokenStorage.getAccessToken();
  const conflict = tabConflictOf(accessToken);
  if (conflict) {
    if (conflict === "signed-out" && sessionNotice.value?.kind === "expired") {
      return "expired";
    }
    if (sessionNotice.value?.kind !== conflict) {
      sessionNotice.value = { kind: conflict };
    }
    return conflict;
  }
  const auth = useAuthStore();
  const refreshToken = tokenStorage.getRefreshToken();
  if (
    auth.info.id &&
    accessToken &&
    refreshToken &&
    accessToken !== auth.tokens.accessToken &&
    !isExpiredToken(refreshToken)
  ) {
    auth.adoptSharedTokens({ accessToken, refreshToken });
    if (sessionNotice.value) {
      const wasExpired = sessionNotice.value.kind === "expired";
      sessionNotice.value = null;
      toastSuccess(
        wasExpired
          ? "다른 탭에서 다시 로그인해 이 화면에서 이어서 저장할 수 있습니다."
          : "다른 탭의 세션이 이 탭과 같아져 이 화면을 계속 쓸 수 있습니다.",
      );
    }
  }
  // 제거 안내(`removed`)도 이 탭 자신의 사건이라 「전환」 을 누를 때까지 둔다.
  if (
    sessionNotice.value &&
    sessionNotice.value.kind !== "expired" &&
    sessionNotice.value.kind !== "removed"
  ) {
    sessionNotice.value = null;
    toastSuccess(
      "다른 탭의 세션이 이 탭과 같아져 이 화면을 계속 쓸 수 있습니다.",
    );
  }
  return sessionNotice.value?.kind ?? null;
}

/** 이 탭의 세션이 끝났다(`clearAuth` — 로그아웃 · 세션 만료 이동 · 로그인 · 회원가입 완료 직전). 안내도 함께 끝낸다(수정 라운드 M1). */
export function clearSessionNotice(): void {
  sessionNotice.value = null;
  pendingRemoval = null;
}

/**
 * 공용 토큰이 아직 이 탭의 세션인가(안내도 함께 맞춘다). 재발급 결과를 저장하기 직전에 다시 본다 — 재발급을 기다리는 사이 다른 탭이
 * 워크스페이스를 바꿨으면 늦게 끝난 이 탭의 저장이 그 전환을 되돌린다(수정 라운드 L2).
 */
export function isTabSessionIntact(): boolean {
  return syncWithSharedTokens() === null;
}

/**
 * 이 탭에서 API 를 보내도 되는지 확인한다. 안 되면 토스트로 짚고 `CanceledError` 를 던진다 — 헬퍼(`getApi` 등)는 취소를 토스트 없는
 * `success: false` 로 돌려준다. 요청 인터셉터 · `getValidatedAccessToken` · 재발급(`reIssueAccessToken`)이 부른다. 재발급 앞에서 막아야 다른
 * 탭이 바꾼 공용 토큰을 이 탭 워크스페이스로 되돌려(재발급은 공용 리프레시 토큰을 쓴다) 반대쪽 탭을 오염시키지 않는다.
 */
export function assertTabSession(): void {
  const kind = syncWithSharedTokens();
  if (!kind) return;
  const now = Date.now();
  if (now - lastBlockedToastAt > BLOCKED_TOAST_INTERVAL_MS) {
    lastBlockedToastAt = now;
    toastWarning(
      kind === "removed" && !sessionNotice.value?.workspaceName
        ? BLOCKED_MESSAGES_NO_WORKSPACE
        : BLOCKED_MESSAGES[kind],
    );
  }
  throw new CanceledError(`Session blocked: ${kind}`);
}

/**
 * 지금 화면에 저장하지 않은 편집(또는 진행 중인 저장)이 있는가. 각 화면이 새로고침 · 탭 닫기용으로 거는 `beforeunload` 처리기의 판단을 그대로
 * 쓴다 — 취소 가능한 가짜 `beforeunload` 를 보내 누군가 `preventDefault()` 하는지 본다. 이탈 가드(`onBeforeRouteLeave`)는 진행 중 저장이면
 * 이동을 막고 토스트를 띄우는 등 화면 동작이 섞여 있어 판단만 따로 쓸 수 없다.
 */
export function hasUnsavedWork(): boolean {
  const probe = new Event("beforeunload", { cancelable: true });
  window.dispatchEvent(probe);
  // 화면 처리기는 「첫 읽기 중」 같은 요청 중(`busy`)도 막는다(69차 CK69-07) — 열자마자 만난 만료 · 제거 401 이 편집 없는 화면을 안내 띠에 붙잡지 않게
  // (RVFA90-04), 레이아웃이 등록한 판정이 있으면 사용자가 이 화면에서 손댄 뒤(포인터 · 키 · 입력)의 처리기 판단만 편집으로 센다.
  const handlerSaysUnsaved =
    probe.defaultPrevented && (editedFieldProbe?.interacted() ?? true);
  return handlerSaysUnsaved || (editedFieldProbe?.edited() ?? false);
}

/** 레이아웃(`DefaultLayout.vue`)이 등록하는 화면 편집 판정 */
export interface EditedFieldProbe {
  /** 이 화면에서 입력칸을 고친 적이 있다 */
  edited: () => boolean;
  /** 이 화면에 들어온 뒤 사용자가 손댔다(포인터 · 키 · 입력) — 화면 처리기의 `beforeunload` 판단이 편집인지 로딩 중인지 가른다 */
  interacted: () => boolean;
  /** 마지막으로 고친 입력칸이 화면에서 사라졌다(권한이 바뀌어 편집 칸이 닫힘) */
  editedFieldGone: () => boolean;
}

/**
 * `beforeunload` 처리기가 없는 쓰기 화면(전체 현황 인라인 편집 등)의 입력은 위 판단이 못 본다 — 레이아웃(`DefaultLayout.vue`)이 「이 화면에서
 * 입력칸을 고친 적이 있다」 판정을 등록해 두면 함께 본다(90차 AU90-06). 처방 A 의 제거 · 강등 401 이 입력 중에 닿아도 입력을
 * 말없이 잃지 않게 한다. 돌려주는 함수는 등록 해제(레이아웃 언마운트).
 */
let editedFieldProbe: EditedFieldProbe | null = null;

export function registerEditedFieldProbe(probe: EditedFieldProbe): () => void {
  editedFieldProbe = probe;
  return () => {
    if (editedFieldProbe === probe) editedFieldProbe = null;
  };
}

/** 마지막으로 고친 입력칸이 화면에서 사라졌는가(등록된 판정이 없으면 모른다 — false). 강등 취소 토스트가 「화면에 그대로」 라고 거짓말하지 않게(RVFA90-02). */
export function editedFieldVanished(): boolean {
  return editedFieldProbe?.editedFieldGone() ?? false;
}

/**
 * 세션이 끝나 로그인 화면으로 보내야 할 때(리프레시 만료 · 재발급 실패 · 401). 저장하지 않은 편집이 없으면 예전처럼 곧바로 보낸다. 있으면
 * 이동하지 않고(`clearAuth` 도 하지 않아 화면이 인증 없이 다시 그려지지 않는다) 만료 안내를 띄운다. 안내가 떠 있는 동안 요청은 모두 막는다.
 * 다른 탭 안내가 이미 떠 있으면 그 안내(새로고침)가 우선이라 아무것도 하지 않는다.
 */
export async function expireSession(
  redirect: string,
  reason: LoginReason = LOGIN_REASON_EXPIRED,
): Promise<void> {
  if (sessionNotice.value) return;
  if (!useAuthStore().info.id || !hasUnsavedWork()) {
    await goLoginPage(redirect, reason);
    return;
  }
  sessionNotice.value = { kind: "expired", redirect };
}

/**
 * 지금 화면의 이탈 가드를 뗀다. 사용자가 "저장하지 않은 편집은 사라진다" 를 이미 확인한 이동(만료 안내의 「로그인으로 이동」 · 로그아웃)에서만
 * 쓴다 — 가드가 다시 묻거나(면접준비 · 면접 콘솔은 `/login` 도 묻는다) 저장을 다시 시도하지 않게 한다. 화면은 곧 언마운트되고, 다시 들어오면
 * 가드를 새로 건다.
 */
export function dropLeaveGuards(): void {
  for (const record of router.currentRoute.value.matched) {
    record.leaveGuards.clear();
  }
}

/** 만료 안내의 「로그인으로 이동」. 돌아올 화면을 실어 보낸다(25차 M25-01). */
export async function proceedToLogin(): Promise<void> {
  const redirect = sessionNotice.value?.redirect ?? currentPathForRedirect();
  dropLeaveGuards();
  sessionNotice.value = null;
  await goLoginPage(redirect, LOGIN_REASON_EXPIRED);
}

/** 제거 안내가 기다리는 전환(대상 워크스페이스와 이미 받은 새 토큰) */
let pendingRemoval: { target: Workspace; tokens: JwtTokens } | null = null;

/**
 * 소속 해제 강제 전환(`recoverFromWorkspaceRemoval`) 직전에 부른다. 저장하지 않은 편집이 없으면 false — 호출부가 곧바로 옮긴다. 있으면
 * 옮기지 않고 제거 안내를 띄워 요청을 막고 true 를 돌려준다(62차 BC62-01). 새 토큰은 「전환」 을 누를 때 쓰고, 이미 회전된 리프레시
 * 토큰만 지금 저장한다(서버는 옛 리프레시를 곧 거절한다, 59차 AR59-01 과 같은 처리). 다른 탭 안내가 이미 떠 있으면 그 안내가 우선이다.
 */
export function deferWorkspaceRemoval(
  target: Workspace,
  tokens: JwtTokens,
): boolean {
  if (sessionNotice.value) return false;
  if (!useAuthStore().info.id || !hasUnsavedWork()) return false;
  useAuthStore().saveRefreshToken(tokens.refreshToken);
  pendingRemoval = { target, tokens };
  sessionNotice.value = { kind: "removed", workspaceName: target.name };
  return true;
}

/**
 * 소속이 하나도 남지 않게 제거됐을 때(리프레시 401 `NOT_JOINED_WORKSPACE`) 쓴다 — 옮겨 갈 워크스페이스가 없다(90차 AU90-06). 저장하지 않은 편집이
 * 없으면 false(호출부가 곧바로 로그인으로 보낸다). 있으면 `workspaceName` 없는 제거 안내를 띄워 요청을 막고 true 를 돌려준다 — 사용자가 입력을
 * 복사한 뒤 「로그인으로 이동」 을 누른다. 「로그인이 만료됐습니다」 로 보내 입력을 말없이 잃던 길(처방 A 의 제거 401)을 막는다.
 */
export function deferRemovalWithoutWorkspace(): boolean {
  if (sessionNotice.value) return false;
  if (!useAuthStore().info.id || !hasUnsavedWork()) return false;
  pendingRemoval = null;
  sessionNotice.value = { kind: "removed" };
  return true;
}

/** 제거 안내의 「○○ 워크스페이스로 전환」. 옮겨 갈 곳이 없는 안내(`deferRemovalWithoutWorkspace`)는 `reason=removed` 로 로그인에 보낸다. 전환하지 못했으면(다른 이동이 끼어듦) 안내를 다시 띄워 한 번 더 누르게 한다. */
export async function proceedToWorkspace(): Promise<void> {
  const pending = pendingRemoval;
  if (!pending) {
    // 옮겨 갈 워크스페이스가 없는 제거 — 「만료」 가 아니라 제거 사유로, 돌아올 화면 없이 로그인으로(RVFA90-03)
    if (sessionNotice.value?.kind === "removed") {
      dropLeaveGuards();
      sessionNotice.value = null;
      await goLoginPage(undefined, LOGIN_REASON_REMOVED);
    }
    return;
  }
  const notice = sessionNotice.value;
  sessionNotice.value = null;
  if (!(await applyWorkspaceSwitch(pending.target, pending.tokens))) {
    sessionNotice.value = notice;
    return;
  }
  pendingRemoval = null;
  toastWarning(workspaceRemovedMessage(pending.target.name));
}

/** 안내에서 「새로고침」 을 눌렀다 — 화면 처리기의 브라우저 이탈 확인을 한 번 더 띄우지 않는다(안내가 이미 편집이 사라진다고 말했다). */
let reloadConfirmed = false;

export function reloadTab(): void {
  reloadConfirmed = true;
  window.location.reload();
}

/** 다른 탭의 토큰 변경을 바로 반영한다(이 탭 자신의 쓰기는 `storage` 이벤트가 오지 않는다). */
function onStorage(event: StorageEvent): void {
  if (event.storageArea !== window.localStorage) return;
  if (
    event.key !== null &&
    event.key !== STORAGE_KEYS.accessToken &&
    event.key !== STORAGE_KEYS.refreshToken
  ) {
    return;
  }
  syncWithSharedTokens();
}

/** 앱 처리기를 화면 처리기보다 먼저 걸어 「새로고침」 확인 뒤에는 화면 처리기까지 가지 않게 한다. */
function onBeforeUnload(event: Event): void {
  if (reloadConfirmed) event.stopImmediatePropagation();
}

/** 사용 중 미리 재발급: 활동을 이 간격보다 자주 보지 않는다. */
const RENEW_CHECK_INTERVAL_MS = 15_000;
/** 액세스 토큰이 이만큼(초) 남으면 미리 재발급한다(운영 액세스 5분). */
const ACCESS_RENEW_BEFORE_SEC = 90;
/** 리프레시 토큰이 이만큼(초) 남으면 미리 재발급한다(액세스가 긴 sandbox · local — 액세스 1440분 · 리프레시 30분). */
const REFRESH_RENEW_BEFORE_SEC = 10 * 60;
/** 미리 재발급이 실패하면 다음 시도를 30초부터 두 배씩 미룬다(상한 5분). 시계가 빨라 새 토큰도 곧 끝나 보이면 상한만큼 미룬다. */
const RENEW_BACKOFF_START_MS = 30_000;
const RENEW_BACKOFF_MAX_MS = 5 * 60_000;
let lastRenewCheckAt = 0;
let renewBackoffMs = 0;
let renewNotBefore = 0;
/** 서버가 401 로 거절한 리프레시 토큰(다른 기기 로그인으로 교체 등). 공용 리프레시가 바뀔 때까지 미리 재발급하지 않는다. */
let renewRejectedFor: string | null = null;

function secondsLeft(token: string): number {
  try {
    const { exp } = jwtDecode(token);
    return exp === undefined ? 0 : exp - Date.now() / 1000;
  } catch {
    return 0;
  }
}

/**
 * 화면을 쓰는 중이면 토큰이 끝나기 전에 미리 재발급한다(S30-02). 리프레시 회전은 재발급 때만 일어나서, 입력만 하고 서버를 부르지 않는
 * 편집(직무분석 인터뷰 정리 · 보고서 · 메일 문안 등)이 25~30분 이어지면 리프레시가 만료돼 저장 때 세션이 끊겼다.
 * - 숨은 탭 · 무활동이면 하지 않는다(이 함수는 활동 · 화면 표시 이벤트에서만 불린다) — 입력이 없는 30분은 지금처럼 만료된다.
 * - 재발급은 스토어의 단일 비행을 함께 쓰고, 실패해도 로그인으로 보내지 않는다(`quiet`). 진짜 만료는 다음 요청이 처리한다.
 * - 다른 탭 토큰이면 하지 않는다 — 재발급이 공용 토큰을 이 탭 워크스페이스로 뒤집는다(요청 때 안내한다).
 * - 실패하면 물러선다(수정 라운드 L4): 401(서버가 리프레시를 거절)이면 그 리프레시로는 다시 하지 않고, 그 밖의 실패는 30초 → 1분 → … 5분으로
 *   미룬다. 다만 401 `INVALID_WORKSPACE_ACCESS`(지금 워크스페이스에서 제거됨)는 거절로 덮지 않고 요청 때와 같은 소속 해제 복구로 보낸다
 *   (62차 BC62-03) — 덮으면 액세스 토큰이 끝날 때까지(sandbox 1440분) 제거된 화면에서 계속 읽고 썼다. 복구하지 못하면 세션 만료로 처리한다. 성공했는데도 새 토큰이 곧 끝나 보이면(클라이언트 시계가 서버보다 빠름) 활동 중 15초마다 회전하지 않게 5분 미룬다.
 */
async function renewWhileActive(): Promise<void> {
  if (document.visibilityState !== "visible" || sessionNotice.value) return;
  // 공개 화면(로그인 · 회원가입 · 직무 입력 · 입사서류 제출)의 입력은 남은 로그인 세션을 쓰는 활동이 아니다 — `App.vue` 시작 처리와 같은 기준.
  if (router.currentRoute.value.matched.some((r) => r.meta.public === true)) {
    return;
  }
  const auth = useAuthStore();
  if (!auth.info.id) return;
  const workspaceId = auth.workspace.id;
  const accessToken = tokenStorage.getAccessToken();
  const refreshToken = tokenStorage.getRefreshToken();
  if (!accessToken || !refreshToken || tabConflictOf(accessToken)) return;
  if (refreshToken === renewRejectedFor || Date.now() < renewNotBefore) return;
  const refreshLeft = secondsLeft(refreshToken);
  // 리프레시가 끝났거나 끝나기 직전이면 재발급이 실패한다 — 저장 때 만료 안내로 처리한다.
  if (refreshLeft < 30) return;
  if (
    secondsLeft(accessToken) > ACCESS_RENEW_BEFORE_SEC &&
    refreshLeft > REFRESH_RENEW_BEFORE_SEC
  ) {
    return;
  }
  resetQuietRenewalResult();
  try {
    await auth.reIssueAccessToken({ quiet: true });
  } catch (e: unknown) {
    logger.warn("Token renewal while active failed:", e);
  }
  const renewed = tokenStorage.getAccessToken();
  const renewedRefresh = tokenStorage.getRefreshToken();
  if (!renewed || renewed === accessToken || !renewedRefresh) {
    if (lastQuietRenewalStatus() === 401) {
      if (workspaceId && quietRenewalRevokedWorkspace()) {
        const recovered = await recoverFromWorkspaceRemoval(workspaceId);
        if (!recovered && !sessionNotice.value) {
          if (quietRenewalNotJoinedAnyWorkspace()) {
            await expireSession("/", LOGIN_REASON_REMOVED);
          } else {
            await expireSession(currentPathForRedirect());
          }
        }
        return;
      }
      renewRejectedFor = refreshToken;
      return;
    }
    renewBackoffMs = Math.min(
      renewBackoffMs ? renewBackoffMs * 2 : RENEW_BACKOFF_START_MS,
      RENEW_BACKOFF_MAX_MS,
    );
    renewNotBefore = Date.now() + renewBackoffMs;
    return;
  }
  renewBackoffMs = 0;
  renewNotBefore =
    secondsLeft(renewed) <= ACCESS_RENEW_BEFORE_SEC ||
    secondsLeft(renewedRefresh) <= REFRESH_RENEW_BEFORE_SEC
      ? Date.now() + RENEW_BACKOFF_MAX_MS
      : 0;
}

function onActivity(): void {
  const now = Date.now();
  if (now - lastRenewCheckAt < RENEW_CHECK_INTERVAL_MS) return;
  lastRenewCheckAt = now;
  void renewWhileActive();
}

function onVisibilityChange(): void {
  if (document.visibilityState === "visible") onActivity();
}

let started = false;

/** 앱 루트(`App.vue`) setup 에서 한 번 부른다 — 화면의 `beforeunload` 처리기보다 먼저 걸려야 한다. */
export function startSessionWatch(): void {
  if (started) return;
  started = true;
  window.addEventListener("storage", onStorage);
  window.addEventListener("beforeunload", onBeforeUnload);
  for (const type of ["pointerdown", "keydown", "input"] as const) {
    window.addEventListener(type, onActivity, { capture: true, passive: true });
  }
  document.addEventListener("visibilitychange", onVisibilityChange);
}
